import React, { useEffect, useCallback } from 'react';

import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, fetchUserAttributes, confirmSignIn } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';

import { useAppState } from '../hooks/useAppState';
import { appActions, CognitoUser } from '../providers/AppStateContext';

const AuthContent: React.FC = ({ children }) => {
  const [, dispatch] = useAppState();
  const { user, authStatus } = useAuthenticator((context) => [
    context.user,
    context.authStatus,
  ]);

  const loadUserData = useCallback(async () => {
    if (authStatus !== 'authenticated' || !user) return;

    try {
      const [session, attributes] = await Promise.all([
        fetchAuthSession(),
        fetchUserAttributes(),
      ]);

      const cognitoGroups =
        (session.tokens?.accessToken?.payload?.['cognito:groups'] as string[]) || [];

      const cognitoUser: CognitoUser = {
        username: user.username,
        cognitoGroups,
        attributes: attributes as Record<string, any>,
      };

      dispatch(appActions.setUser(cognitoUser));
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  }, [authStatus, user, dispatch]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  if (authStatus !== 'authenticated') {
    return null;
  }

  return <>{children}</>;
};

// Amplify UI v6 passes all forceNewPassword form fields as user attributes to confirmSignIn.
// Strip read-only/identifier fields that are not updatable during sign-in.
const EXCLUDED_SIGN_IN_ATTRIBUTES = new Set(['username', 'email', 'sub']);

const services = {
  async handleConfirmSignIn(input: any) {
    const { options, ...rest } = input as any;
    const filteredAttributes = Object.fromEntries(
      Object.entries(options?.userAttributes || {}).filter(
        ([key]) => !EXCLUDED_SIGN_IN_ATTRIBUTES.has(key)
      )
    );
    return confirmSignIn({
      ...rest,
      options: { ...options, userAttributes: filteredAttributes },
    });
  },
};

export const Auth: React.FC = ({ children }) => {
  return (
    <Authenticator
      hideSignUp={true}
      services={services}
      formFields={{
        setupTotp: {
          QR: {
            totpIssuer: 'Beautypass',
          },
        },
      }}
    >
      <AuthContent>{children}</AuthContent>
    </Authenticator>
  );
};
